import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { User } from '../types/user';
import { getUserInfo } from '../api/apiCalls';

interface AppDataContextType {
  setUser: React.Dispatch<React.SetStateAction<User | null | undefined>>;
  user: User | null | undefined;
  fetchUser: () => Promise<void>;
}

const UserContext = createContext<AppDataContextType | undefined>(undefined);

interface UserContextProviderProps {
  children: ReactNode;
}

export const UserContextProvider: React.FC<UserContextProviderProps> = ({ children }) => {
  // 1) Start with undefined => “still loading”
  const [user, setUser] = useState<User | null | undefined>(undefined);

  // 2) A function to fetch user info from the server
  async function fetchUser() {
    try {
      const u = await getUserInfo();
      setUser(u);  // user object if logged in
    } catch (err) {
      console.error("Fetching user failed:", err);
      setUser(null); // means “not logged in” or error
    }
  }

  // 3) On mount, call fetchUser() once
  useEffect(() => {
    fetchUser();
  }, []);

  const contextValue: AppDataContextType = {
    setUser,
    user,
    fetchUser,
  };

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const ctx = useContext(UserContext);
  if (!ctx) {
    throw new Error('useUser must be used within a UserContextProvider');
  }
  return ctx;
};
